import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import { debounce } from 'lodash';
import { FaSearch, FaTimes, FaSpinner, FaInfoCircle, FaShoppingCart, FaFileUpload, FaPhone, FaComment } from 'react-icons/fa';
import medHeroImage from "../assets/hero.webp";


// Set app element for accessibility
Modal.setAppElement('#root');

// Custom modal styles to prevent body scroll
const customModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: '90vh',
    overflowY: 'auto',
    width: '90%',
    maxWidth: '800px',
    padding: '0',
    border: 'none',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  }
};

const Services = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allMedicines, setAllMedicines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isPrescriptionSubmitting, setIsPrescriptionSubmitting] = useState(false);
  const [prescriptionSuccess, setPrescriptionSuccess] = useState(false);
  const medicinesPerPage = 9;

  // Common medicines for default view and suggestions
  const commonMedicines = [
    "Betadine", "Paracetamol", "Ibuprofen", 
    "Amoxicillin", "Cetirizine", "Omeprazole",
    "Aspirin", "Diclofenac", "Loratadine",
    "Atorvastatin", "Metformin", "Losartan",
    "Levothyroxine", "Amlodipine", "Sertraline"
  ];

  // Truncated Text Component
  const TruncatedText = ({ text, maxLength = 100, className = "" }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    if (!text) return <p className={`text-gray-600 ${className}`}>Not specified</p>;
    
    if (text.length <= maxLength || isExpanded) {
      return (
        <div>
          <p className={`text-gray-600 ${className}`}>{text}</p>
          {text.length > maxLength && (
            <button 
              onClick={() => setIsExpanded(false)}
              className="text-blue-500 text-sm mt-1 hover:underline flex items-center"
            >
              <FaTimes className="mr-1" /> Show less
            </button>
          )}
        </div>
      );
    }

    return (
      <div>
        <p className={`text-gray-600 ${className}`}>
          {text.substring(0, maxLength)}...
        </p>
        <button 
          onClick={() => setIsExpanded(true)}
          className="text-blue-500 text-sm mt-1 hover:underline flex items-center"
        >
          <FaInfoCircle className="mr-1" /> Show more
        </button>
      </div>
    );
  };

  // Fetch medicine details from API
  const fetchMedicineDetails = async (medicineName) => {
    try {
      const response = await axios.post('https://www.pcds.co.in/medapi.php', { 
        uri: medicineName 
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${medicineName}:`, error);
      return null;
    }
  };

  // Debounced search for suggestions
  const fetchSuggestions = useCallback(
    debounce(async (term) => {
      if (!term.trim()) {
        setSuggestions([]);
        return;
      }

      try {
        // Filter common medicines that match the search term
        const matched = commonMedicines.filter(med => 
          med.toLowerCase().includes(term.toLowerCase())
        ).slice(0, 5);
        
        setSuggestions(matched);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    }, 300),
    []
  );

  // Load all default medicines on initial render
  useEffect(() => {
    const fetchAllDefaultMedicines = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Load recent searches from localStorage
        const savedSearches = JSON.parse(localStorage.getItem('recentMedicineSearches')) || [];
        setRecentSearches(savedSearches);
        
        // Fetch all common medicines to display by default
        const medicinePromises = commonMedicines.map(medicine => 
          fetchMedicineDetails(medicine)
        );
        
        const results = await Promise.all(medicinePromises);
        const successfulResults = results.filter(medicine => medicine !== null);
        
        setAllMedicines(successfulResults);
      } catch (error) {
        console.error('Error fetching default medicines:', error);
        setError('Failed to load medicines. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllDefaultMedicines();
  }, []);

  // Handle search term changes for suggestions
  useEffect(() => {
    if (searchTerm.trim()) {
      fetchSuggestions(searchTerm);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, fetchSuggestions]);

  // Search for medicines
  const fetchMedicines = async (term = searchTerm) => {
    if (!term.trim()) return;
    
    setLoading(true);
    setError(null);
    setShowSuggestions(false);
    
    try {
      const medicine = await fetchMedicineDetails(term);
      
      if (medicine) {
        setAllMedicines([medicine]);
        
        // Add to recent searches
        const newRecentSearches = [term, ...recentSearches.filter(item => item !== term)].slice(0, 5);
        setRecentSearches(newRecentSearches);
        localStorage.setItem('recentMedicineSearches', JSON.stringify(newRecentSearches));
      } else {
        setAllMedicines([]);
        setError(`No medicines found for "${term}"`);
      }
      
      setCurrentPage(1);
    } catch (error) {
      console.error('Error searching medicines:', error);
      setError('An error occurred while searching. Please try again.');
      setAllMedicines([]);
    } finally {
      setLoading(false);
    }
  };

  // Reset to all default medicines
  const handleReset = async () => {
    setSearchTerm('');
    setLoading(true);
    setError(null);
    setShowSuggestions(false);
    
    try {
      const medicinePromises = commonMedicines.map(medicine => 
        fetchMedicineDetails(medicine)
      );
      
      const results = await Promise.all(medicinePromises);
      const successfulResults = results.filter(medicine => medicine !== null);
      
      setAllMedicines(successfulResults);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error resetting medicines:', error);
      setError('Failed to reset medicines. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload for prescription
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPrescriptionSuccess(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit prescription
  const handlePrescriptionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;
    
    setIsPrescriptionSubmitting(true);
    
    try {
      // Simulate API upload
      await new Promise(resolve => setTimeout(resolve, 1500));
      setPrescriptionSuccess(true);
      setSelectedFile(null);
      setPreviewImage(null);
    } catch (error) {
      console.error('Error uploading prescription:', error);
    } finally {
      setIsPrescriptionSubmitting(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    fetchMedicines();
  };

  // Open modal with medicine details
  const openModal = (medicine) => {
    document.body.style.overflow = 'hidden'; // Prevent body scroll
    setSelectedMedicine(medicine);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    document.body.style.overflow = 'auto'; // Re-enable body scroll
    setIsModalOpen(false);
    setSelectedMedicine(null);
  };

  // Pagination logic - now using allMedicines
  const indexOfLastMedicine = currentPage * medicinesPerPage;
  const indexOfFirstMedicine = indexOfLastMedicine - medicinesPerPage;
  const currentMedicines = allMedicines.slice(indexOfFirstMedicine, indexOfLastMedicine);
  const totalPages = Math.ceil(allMedicines.length / medicinesPerPage);

  // Medicine Card Component
  const MedicineCard = ({ medicine }) => {
    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-gray-100 h-full flex flex-col">
        <div className="p-6 flex-grow">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-1">
                {medicine.brand_name || 'Unnamed Medicine'}
              </h3>
              <p className="text-purple-600 font-medium line-clamp-1">
                {medicine.generic_name || 'No generic name'}
              </p>
            </div>
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full whitespace-nowrap">
              #{medicine.product_id?.substring(0, 6) || 'N/A'}
            </span>
          </div>
          
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700">Purpose:</h4>
            <TruncatedText 
              text={medicine.purpose || medicine.json_data?.purpose?.[0]} 
              maxLength={120} 
            />
          </div>
          
          <div className="mt-4">
            <h4 className="font-semibold text-gray-700">Uses:</h4>
            <TruncatedText 
              text={medicine.use_case || medicine.json_data?.indications_and_usage?.[0]} 
              maxLength={120} 
            />
          </div>
          
          {medicine.json_data?.active_ingredient && (
            <div className="mt-4">
              <h4 className="font-semibold text-gray-700">Active Ingredient:</h4>
              <TruncatedText 
                text={medicine.json_data.active_ingredient[0]} 
                maxLength={120} 
              />
            </div>
          )}
        </div>
        
        <div className="mt-auto pt-4 border-t border-gray-100 p-6 flex gap-2">
          <button 
            onClick={() => openModal(medicine)}
            className="flex-grow bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 font-medium flex items-center justify-center"
          >
            <FaInfoCircle className="mr-2" /> Details
          </button>
          <button className="flex-grow bg-gradient-to-r from-green-500 to-green-600 text-white py-2 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 font-medium flex items-center justify-center">
            <FaShoppingCart className="mr-2" /> Buy
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div 
        className="relative h-96 w-full bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: `url(${medHeroImage})` }}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
              Medicine Information Center
            </span>
          </h1>
          <p className="text-xl text-white max-w-2xl mx-auto">
            Comprehensive drug information at your fingertips
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="container mx-auto px-4 -mt-12 relative z-20">
        <form 
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-xl p-6 max-w-4xl mx-auto relative"
        >
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-grow">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Search for medicines (e.g., Betadine, Paracetamol)"
                className="w-full px-4 py-3 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              
              {/* Suggestions dropdown */}
              {(showSuggestions && (suggestions.length > 0 || recentSearches.length > 0)) && (
                <div className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
                  {recentSearches.length > 0 && (
                    <div className="px-4 py-2 text-sm text-gray-500 border-b">Recent Searches</div>
                  )}
                  {recentSearches.map((search, index) => (
                    <div 
                      key={`recent-${index}`}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center"
                      onClick={() => {
                        setSearchTerm(search);
                        fetchMedicines(search);
                      }}
                    >
                      <FaSearch className="mr-2 text-gray-400" />
                      {search}
                    </div>
                  ))}
                  
                  {suggestions.length > 0 && (
                    <div className="px-4 py-2 text-sm text-gray-500 border-b">Suggestions</div>
                  )}
                  {suggestions.map((suggestion, index) => (
                    <div 
                      key={`suggestion-${index}`}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setSearchTerm(suggestion);
                        fetchMedicines(suggestion);
                      }}
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 font-medium flex items-center"
                disabled={loading || !searchTerm.trim()}
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Searching...
                  </>
                ) : (
                  <>
                    <FaSearch className="mr-2" />
                    Search
                  </>
                )}
              </button>
              {(allMedicines.length > 0 || searchTerm) && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-300 font-medium flex items-center"
                >
                  <FaTimes className="mr-2" />
                  Reset
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Medicine List */}
      <div className="container mx-auto px-4 py-12">
        {loading && allMedicines.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <FaSpinner className="animate-spin text-4xl text-blue-500" />
          </div>
        ) : (
          <>
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                {searchTerm ? 'Search Results' : 'Common Medicines'}
              </span>
            </h2>
            
            {error ? (
              <div className="text-center py-12">
                <div className="bg-red-50 border-l-4 border-red-500 p-4 max-w-md mx-auto mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FaTimes className="h-5 w-5 text-red-500" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
                {searchTerm && (
                  <button
                    onClick={handleReset}
                    className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-300 font-medium flex items-center mx-auto"
                  >
                    <FaSearch className="mr-2" />
                    Show Common Medicines
                  </button>
                )}
              </div>
            ) : currentMedicines.length > 0 ? (
              <>
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {currentMedicines.map((medicine, index) => (
                    <MedicineCard medicine={medicine} key={index} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-10">
                    <nav className="inline-flex rounded-md shadow">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-l-md hover:bg-gray-50 disabled:opacity-50 flex items-center"
                      >
                        Previous
                      </button>
                      
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-4 py-2 border ${currentPage === pageNum 
                              ? 'bg-blue-500 text-white border-blue-500' 
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-r-md hover:bg-gray-50 disabled:opacity-50 flex items-center"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 max-w-md mx-auto mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <FaInfoCircle className="h-5 w-5 text-yellow-500" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">No medicines found</h3>
                    </div>
                  </div>
                </div>
                <p className="text-gray-500 mt-2">
                  Try searching with a different term or reset to see common medicines
                </p>
                <button
                  onClick={handleReset}
                  className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-300 font-medium flex items-center mx-auto"
                >
                  <FaSearch className="mr-2" />
                  Show Common Medicines
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Medicine Details Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Medicine Details"
        style={customModalStyles}
        onAfterOpen={() => {
          document.body.style.overflow = 'hidden';
        }}
        onAfterClose={() => {
          document.body.style.overflow = 'auto';
        }}
      >
        {selectedMedicine && (
          <div className="space-y-6 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedMedicine.brand_name}
                </h2>
                <p className="text-lg text-purple-600">
                  {selectedMedicine.generic_name}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">Active Ingredients</h3>
                <TruncatedText 
                  text={selectedMedicine.json_data?.active_ingredient?.[0] || 'Not specified'} 
                  className="text-gray-600"
                />
              </div>
              
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">Inactive Ingredients</h3>
                <TruncatedText 
                  text={selectedMedicine.inactive_ingredient || selectedMedicine.json_data?.inactive_ingredient?.[0] || 'Not specified'} 
                  className="text-gray-600"
                />
              </div>
              
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">Purpose</h3>
                <TruncatedText 
                  text={selectedMedicine.purpose || selectedMedicine.json_data?.purpose?.[0] || 'Not specified'} 
                  className="text-gray-600"
                />
              </div>
              
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">Uses</h3>
                <TruncatedText 
                  text={selectedMedicine.use_case || selectedMedicine.json_data?.indications_and_usage?.[0] || 'Not specified'} 
                  className="text-gray-600"
                />
              </div>
              
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">Warnings</h3>
                <TruncatedText 
                  text={selectedMedicine.json_data?.warnings?.[0] || 'Not specified'} 
                  className="text-gray-600"
                />
              </div>
              
              <div>
                <h3 className="font-bold text-lg text-gray-800 mb-2">Directions</h3>
                <TruncatedText 
                  text={selectedMedicine.json_data?.dosage_and_administration?.[0] || 'Not specified'} 
                  className="text-gray-600"
                />
              </div>
              
              {selectedMedicine.json_data?.do_not_use?.[0] && (
                <div className="md:col-span-2 bg-red-50 p-4 rounded-lg">
                  <h3 className="font-bold text-lg text-red-800 mb-2">Do Not Use</h3>
                  <TruncatedText 
                    text={selectedMedicine.json_data.do_not_use[0]} 
                    className="text-red-600"
                  />
                </div>
              )}
              
              {selectedMedicine.json_data?.ask_doctor?.[0] && (
                <div className="md:col-span-2 bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-bold text-lg text-blue-800 mb-2">Ask a Doctor</h3>
                  <TruncatedText 
                    text={selectedMedicine.json_data.ask_doctor[0]} 
                    className="text-blue-600"
                  />
                </div>
              )}
              
              {selectedMedicine.json_data?.stop_use?.[0] && (
                <div className="md:col-span-2 bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-bold text-lg text-yellow-800 mb-2">Stop Use</h3>
                  <TruncatedText 
                    text={selectedMedicine.json_data.stop_use[0]} 
                    className="text-yellow-600"
                  />
                </div>
              )}
              
              {selectedMedicine.json_data?.storage_and_handling?.[0] && (
                <div className="md:col-span-2 bg-green-50 p-4 rounded-lg">
                  <h3 className="font-bold text-lg text-green-800 mb-2">Storage Instructions</h3>
                  <TruncatedText 
                    text={selectedMedicine.json_data.storage_and_handling[0]} 
                    className="text-green-600"
                  />
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-300 flex items-center"
              >
                <FaTimes className="mr-2" /> Close
              </button>
              <button className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 flex items-center">
                <FaShoppingCart className="mr-2" /> Buy Now
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Prescription Upload */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/2 p-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                    Upload Your Prescription
                  </span>
                </h2>
                <p className="text-gray-600 mb-6">
                  Simply upload your doctor's prescription and we'll prepare your medications for quick pickup or delivery.
                </p>
                <form onSubmit={handlePrescriptionSubmit}>
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="prescription">
                      Upload Prescription (JPG, PNG, PDF)
                    </label>
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col w-full h-32 border-4 border-dashed hover:bg-gray-100 hover:border-gray-300 transition-all duration-300 rounded-lg cursor-pointer">
                        <div className="flex flex-col items-center justify-center pt-7">
                          <FaFileUpload className="text-3xl text-gray-400" />
                          <p className="pt-1 text-sm tracking-wider text-gray-600">
                            {selectedFile ? selectedFile.name : 'Select a file'}
                          </p>
                        </div>
                        <input 
                          type="file" 
                          id="prescription"
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                          className="opacity-0 absolute" 
                        />
                      </label>
                    </div>
                  </div>
                  
                  {prescriptionSuccess && (
                    <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-green-700">
                            Prescription uploaded successfully! We'll contact you shortly.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={!selectedFile || isPrescriptionSubmitting}
                    className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isPrescriptionSubmitting ? (
                      <>
                        <FaSpinner className="animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <FaFileUpload className="mr-2" />
                        Submit Prescription
                      </>
                    )}
                  </button>
                </form>
              </div>
              <div className="md:w-1/2 bg-gray-100 flex items-center justify-center p-8">
                {previewImage ? (
                  <div className="relative">
                    <img 
                      src={previewImage} 
                      alt="Prescription preview" 
                      className="max-h-64 rounded-lg shadow-md"
                    />
                    <button
                      onClick={() => {
                        setPreviewImage(null);
                        setSelectedFile(null);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <FaTimes className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <FaFileUpload className="mx-auto h-12 w-12" />
                    <p className="mt-2">Prescription preview will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 py-16 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Need Help Finding the Right Medicine?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our pharmacists are available 24/7 to answer your questions and help you find the right medication.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button className="px-8 py-3 bg-white text-blue-600 rounded-lg hover:bg-gray-100 transition-all duration-300 font-medium flex items-center justify-center">
              <FaComment className="mr-2" /> Chat with a Pharmacist
            </button>
            <button className="px-8 py-3 border-2 border-white text-white rounded-lg hover:bg-white hover:text-blue-600 transition-all duration-300 font-medium flex items-center justify-center">
              <FaPhone className="mr-2" /> Call Now: 1-800-MED-HELP
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;